import {Component, inject, Input, NgZone, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormWrapper} from "../items/form.wrapper";
import {FormTextEditorConfig} from "./form-text-editor.config";
import {Subscription} from "rxjs";
import {AbstractFormFieldComponent} from "../abstract-form-field.component";
import {FormLabelComponent} from "../form-label/form-label.component";

import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import {FormFieldErrorComponent} from "../form-field-error/form-field-error.component";
import {Blur, ContentChange, QuillEditorComponent, QuillModules} from "ngx-quill";
import {SettingKey} from "../../../base/setting-key.enum";
import {SettingDto} from "../../../repositories/dto/setting.dto";
import {SettingsService} from "../../../services/settings.service";
import {isBlank, unsubscribe} from "../../utils/epic.utils";
import {FormStateEnum} from "../items/form-state.enum";
import {filter, map} from "rxjs/operators";
import {Size} from "../../../base/data/size";

@Component({
  selector: 'acc-form-text-editor',
  imports: [
    FormLabelComponent,
    ReactiveFormsModule,
    FormFieldErrorComponent,
    QuillEditorComponent,
    FormsModule
  ],
  templateUrl: './form-text-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './form-text-editor.component.css'
})
export class FormTextEditorComponent extends AbstractFormFieldComponent implements OnInit, OnDestroy {

  @Input({required: true})
  public name!: string;

  @Input({required: true})
  public wrapper!: FormWrapper;

  @Input()
  public config: FormTextEditorConfig = new FormTextEditorConfig();

  protected dir: string | undefined;
  protected editorModel: string | null = null;

  private readonly subs: Subscription[] = [];
  private timeout: any;
  private content: string | null = null;

  private readonly settingsService: SettingsService = inject(SettingsService);
  private readonly zone: NgZone = inject(NgZone);

  ngOnInit(): void {
    if (this.isViewOnly()) {
      this.config.quillModules.toolbar = false;
    }
    this.subs.push(
      this.settingsService.getSettingOnChange(SettingKey.USER_LAYOUT_DIRECTION)
        .subscribe((kv: SettingDto) => this.dir = kv.value),
      this.wrapper.onReady().subscribe(() => this.initValue()),
      // Workaround: when used wrapper.setValue
      this.wrapper.onChangeDistinctNameValue().pipe(
        filter((c) => c.getName() === this.name),
        filter((c) => c.getValue() !== this.config),
        map((c) => c.getValue())
      ).subscribe((v) => {
        this.editorModel = v;
        this.content = v;
      })
    );
  }

  ngOnDestroy(): void {
    unsubscribe(this.subs);
  }

  protected override isViewOnly(): boolean {
    let viewOnly: boolean = this._isViewOnly(this.name, this.wrapper);
    if (viewOnly && this.wrapper.isReady) {
      viewOnly = this._isDisabled(this.name, this.wrapper);
    }
    return viewOnly;
  }

  protected override getSize(): Size | undefined {
    return this.config.size;
  }

  protected isInvalid(): boolean {
    return this._isInvalid(this.name, this.wrapper);
  }

  protected getDir(): string {
    return this.dir ? this.dir : 'ltr';
  }

  // ngx-quill builds the editor outside the Angular zone and, unlike up to version 28, no longer brings its events back. Without zone.run the
  // save timer ran unseen, and the next change detection wrote the text from before the pause into the editor - the cursor jumped to the start.
  protected accContentChanged(quill: ContentChange): void {
    this.zone.run(() => {
      if (!this.isViewOnly() && quill.source === 'user' && this.editorModel !== quill.html) {
        this.content = quill.html;
        this.wrapper.updateState(FormStateEnum.CHANGED);
        if (this.config.patchValueOnChange) {
          this.accInputPatch(this.content);
        }
        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => this.accInputChange(this.content), this.config.keyDelay);
      }
    });
  }

  protected accContentLeave(quill: Blur): void {
    this.zone.run(() => {
      if (!this.isViewOnly() && quill.source === 'user') {
        clearTimeout(this.timeout);
        this._onChangeValue(this.name, this.wrapper, this.content, this.editorModel);
      }
    });
  }

  protected isHidden(): boolean {
    return this.isViewOnly() && this.config.hiddenIfEmpty && isBlank(this.wrapper.getValue(this.name));
  }

  protected getModules(): QuillModules {
    if (this.isViewOnly()) {
      return FormTextEditorConfig.buildEmptyQuillConfig();
    }
    return this.config.quillModules;
  }

  private accInputChange(value: string | null): void {
    this._onChangeValue(this.name, this.wrapper, value, value + ' ');
  }

  private accInputPatch(value: string | null): void {
    this._onChangeValue(this.name, this.wrapper, value, undefined, true);
  }

  private initValue(): void {
    this.editorModel = this.wrapper.getValue(this.name);
    this.content = this.editorModel;
  }
}
